import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { SceneBubbles } from './SceneChat';
import {
  isBookOpen, isHolder, bookSeq, serverNowFor, canTurnPage, canTakeBookmark, canAskBookmark,
  askForHolder, bookmarkWords, bookmarkLoose, pageLine, shelfBook, chapterForPage, chapterAfter,
  wantsNextChapter, parasForPage, loadChapter, staleTurnRetrySeq,
} from '../../utils/houseLibrary';

/**
 * 📖 BookReader: the House library's shared page (HOUSE_LIBRARY_SPEC_2026_09_30.md
 * §3.3-§3.4; Nina's mockup picks 09-30: desktop B "Beside the chat", phone B
 * "Full screen with chat bubbles").
 *   · layout 'split'  desktop: a column BESIDE the chat (the middle column splits)
 *   · layout 'stack'  desktop, a middle column too narrow to split: above the chat
 *   · layout 'phone'  the page fills the phone, the newest chat lines float over
 *                     it as bubbles (the scene chat's), "Chat ▾" folds it to the
 *                     one-line bar (BookFoldBar). The message box stays below it.
 * The page is everyone's page: turns go through `onAct` (bookControl), never a
 * chat send. Only the holder turns (and anyone once the bookmark is loose, 60 s);
 * "Ask for it" pings the holder for 12 s ("Give" · "Keep").
 * TEXT NODES ONLY: a book's words are never HTML here.
 * Theme armor: every class is `hlb-` (no card/panel/chip/message/btn/badge/tag/
 * user/player/header/author/surface/input/button/primary inside), every button
 * carries data-prop-ui + doubled-class !important rules. ⚠️ No global
 * box-sizing in this app: every padded box sets it. Player copy: no em dashes.
 */
export const READER_CSS = `
.hlb-rd, .hlb-rd * { box-sizing: border-box; }
.hlb-rd { display: flex; flex-direction: column; min-height: 0; min-width: 0; background: #f3e8d0; color: #2b2118; font-family: Literata, Georgia, "Times New Roman", serif; text-shadow: none; text-align: left; border-radius: 10px; overflow: hidden; box-shadow: 0 10px 30px rgba(0,0,0,.45), 0 0 0 1px rgba(242,179,94,.28); letter-spacing: normal; text-transform: none; }
.hlb-rd[data-book-reader="stack"] { max-height: min(46%, 360px); flex: 0 1 auto; order: -1; }
.hlb-rd[data-book-reader="split"] { flex: 1.05 1 0%; height: auto; align-self: stretch; }
.hlb-rd[data-book-reader="phone"] { position: fixed; left: 0; right: 0; top: 0; z-index: 4960; border-radius: 0; box-shadow: none; transition: opacity .35s ease; }
.hlb-rd[data-curtain="1"] { opacity: 0; pointer-events: none; }
.hlb-band { flex: none; display: flex; align-items: center; gap: 8px; height: 44px; padding: 0 60px 0 14px; background: #1a1411; border-bottom: 1px solid #3b2d23; color: #f3e9d8; font: 600 13px/1 Literata, Georgia, serif; }
.hlb-top { flex: none; display: flex; align-items: baseline; gap: 10px; padding: 11px 14px 7px 16px; border-bottom: 1px solid rgba(43,33,24,.15); min-width: 0; }
.hlb-ttl { font-weight: 600; font-size: 16px; line-height: 1.25; color: #2b2118; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; flex: 0 1 auto; }
.hlb-ch { font-size: 12.5px; color: #7a6a55; font-style: italic; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; flex: 1 1 auto; }
.hlb-sleep { flex: none; padding: 6px 16px; font: italic 12.5px/1.35 Literata, Georgia, serif; color: #7a6a55; background: rgba(43,33,24,.05); border-bottom: 1px solid rgba(43,33,24,.1); }
.hlb-pg { flex: 1 1 auto; min-height: 0; overflow-y: auto; overscroll-behavior: contain; padding: 12px 20px 14px; font-size: 16px; line-height: 1.6; color: #2b2118; }
.hlb-pg p { margin: 0; white-space: pre-line; overflow-wrap: break-word; }
.hlb-pg p + p { margin-top: .7em; }
.hlb-pg p.hlb-brk { text-align: center; color: #7a6a55; letter-spacing: .3em; }
.hlb-pg .hlb-wait { color: #7a6a55; font-style: italic; }
.hlb-pg[data-stale="1"] > p:not(.hlb-wait) { opacity: .45; }
.hlb-rd[data-book-reader="phone"] .hlb-pg { font-size: 15px; line-height: 1.62; padding: 12px 16px 150px; }
.hlb-row { flex: none; display: flex; align-items: center; flex-wrap: wrap; gap: 6px 10px; padding: 7px 14px 7px 16px; border-top: 1px solid rgba(43,33,24,.12); font: 500 13px/1.3 ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif; color: #5c4b38; background: rgba(43,33,24,.05); }
.hlb-row b { color: #2b2118; }
.hlb-ft { flex: none; display: flex; align-items: center; gap: 10px; padding: 7px 14px 8px 16px; border-top: 1px solid rgba(43,33,24,.15); font: 500 12.5px/1 ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif; color: #7a6a55; min-width: 0; flex-wrap: wrap; }
.hlb-pn { font-variant-numeric: tabular-nums; white-space: nowrap; }
.hlb-mk { margin-left: auto; color: #8a5a1c; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
.hlb-sp { flex: 1 1 auto; min-width: 0; }
.hlb-bub { position: absolute; left: 0; right: 0; bottom: 0; padding: 30px 12px 8px; background: linear-gradient(rgba(243,232,208,0), rgba(243,232,208,.96) 22%); pointer-events: none; max-height: 46%; overflow: hidden; display: flex; flex-direction: column; justify-content: flex-end; }
.hlb-pwrap { position: relative; flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; }
.hlb-rd .hlb-turn.hlb-turn { all: unset; box-sizing: border-box; display: inline-grid; place-items: center; width: 32px; height: 32px; flex: none; border-radius: 50% !important; border: 1px solid rgba(43,33,24,.32) !important; background: rgba(255,255,255,.35) !important; color: #2b2118 !important; font: 600 17px/1 Georgia, serif !important; cursor: pointer; box-shadow: none !important; text-shadow: none !important; transform: none !important; position: static !important; overflow: visible !important; margin: 0 !important; padding: 0 !important; min-width: 0 !important; min-height: 0 !important; }
.hlb-rd[data-book-reader="phone"] .hlb-turn.hlb-turn { width: 40px; height: 40px; font-size: 20px !important; }
.hlb-rd .hlb-turn.hlb-turn:hover { background: rgba(255,255,255,.65) !important; transform: none !important; box-shadow: none !important; }
.hlb-rd .hlb-turn.hlb-turn[disabled] { opacity: .35; cursor: default; }
.hlb-rd .hlb-lnk.hlb-lnk { all: unset; box-sizing: border-box; display: inline-flex; align-items: center; gap: 4px; min-height: 28px; padding: 0 2px !important; margin: 0 !important; border: 0 !important; background: none !important; color: #7a5a2c !important; font: 600 12.5px/1 ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif !important; cursor: pointer; text-decoration: underline; text-decoration-color: rgba(122,90,44,.4); text-underline-offset: 3px; box-shadow: none !important; text-shadow: none !important; transform: none !important; white-space: nowrap; position: static !important; overflow: visible !important; min-width: 0 !important; }
.hlb-rd .hlb-lnk.hlb-lnk:hover { color: #2b2118 !important; transform: none !important; box-shadow: none !important; }
.hlb-rd .hlb-x.hlb-x { all: unset; box-sizing: border-box; display: inline-flex; align-items: center; min-height: 28px; padding: 0 4px !important; margin: 0 0 0 auto !important; border: 0 !important; background: none !important; color: #7a6a55 !important; font: 600 12.5px/1 ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif !important; cursor: pointer; box-shadow: none !important; text-shadow: none !important; transform: none !important; white-space: nowrap; flex: none; position: static !important; overflow: visible !important; min-width: 0 !important; }
.hlb-rd .hlb-x.hlb-x + .hlb-x.hlb-x { margin-left: 8px !important; }
.hlb-rd .hlb-x.hlb-x:hover { color: #2b2118 !important; transform: none !important; box-shadow: none !important; }
.hlb-rd .hlb-pill.hlb-pill { all: unset; box-sizing: border-box; display: inline-flex; align-items: center; justify-content: center; height: 30px; padding: 0 12px !important; margin: 0 !important; border-radius: 999px !important; border: 1px solid rgba(43,33,24,.3) !important; background: rgba(255,255,255,.4) !important; color: #2b2118 !important; font: 700 12.5px/1 ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif !important; cursor: pointer; box-shadow: none !important; text-shadow: none !important; transform: none !important; white-space: nowrap; position: static !important; overflow: visible !important; min-width: 0 !important; }
.hlb-rd .hlb-pill.hlb-gold.hlb-gold { background: #f2b35e !important; border-color: #c98c3c !important; color: #1b140f !important; }
.hlb-rd .hlb-pill.hlb-pill:hover { filter: brightness(1.05); transform: none !important; box-shadow: none !important; }
.hlb-rd .hlb-turn:focus-visible, .hlb-rd .hlb-lnk:focus-visible, .hlb-rd .hlb-x:focus-visible, .hlb-rd .hlb-pill:focus-visible { outline: 2px solid #a8773a !important; outline-offset: 2px; }
@media (prefers-reduced-motion: reduce) { .hlb-rd[data-book-reader="phone"] { transition: none; } }
`;

/** The folded phone bar: "📖 Frankenstein · p. 2 · Open ▾" (it rides the top of the message dock). */
export const FOLD_CSS = `
.hlb-fold.hlb-fold.hlb-fold { all: unset; box-sizing: border-box; display: flex; align-items: center; gap: 8px; width: 100%; min-height: 40px; margin: 0 0 6px !important; padding: 8px 12px !important; border-radius: 10px !important; background: #2a2019 !important; border: 1px solid #a8773a !important; color: #f3e9d8 !important; font: 500 13px/1.25 ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif !important; cursor: pointer; text-shadow: none !important; box-shadow: none !important; transform: none !important; position: static !important; overflow: hidden !important; text-align: left; }
.hlb-fold.hlb-fold.hlb-fold b { font-family: Literata, Georgia, serif; font-weight: 600; color: #f3e9d8; }
.hlb-fold .hlb-fw { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hlb-fold .hlb-fo { flex: none; color: #f2b35e; font-weight: 700; }
.hlb-fold.hlb-fold.hlb-fold:focus-visible { outline: 2px solid #f2b35e !important; outline-offset: 2px; }
`;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const NOTE_MS = 4500;

/** "📖 Frankenstein · p. 2 · Open ▾": one tap opens the full-screen page again. */
export function BookFoldBar({ book, onOpen }) {
  if (!isBookOpen(book)) return null;
  return (
    <>
      <style>{FOLD_CSS}</style>
      <button type="button" className="hlb-fold" data-prop-ui="" data-book-bar="1"
        aria-label={`Open ${book.title || 'the book'} again, page ${book.page}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => { if (onOpen) onOpen(); }}>
        <span aria-hidden="true">📖</span>
        <span className="hlb-fw"><b>{book.title || 'A book'}</b> · p. {book.page}</span>
        <span className="hlb-fo" aria-hidden="true">Open ▾</span>
      </button>
    </>
  );
}

function useChapter(base, slug, file, nonce) {
  const key = `${base}|${slug}|${file}`;
  const [chap, setChap] = useState({ key: null, json: null, err: false });
  useEffect(() => {
    if (!base || !slug || !file) return undefined;
    let alive = true;
    // "Try again": back to "finding" while it really fetches again (a failure left the cache)
    setChap((c) => (c.key === key && c.err ? { key, json: null, err: false } : c));
    loadChapter(base, slug, file)
      .then((json) => { if (alive) setChap({ key, json, err: false }); })
      .catch(() => { if (alive) setChap({ key, json: null, err: true }); });
    return () => { alive = false; };
  }, [key, base, slug, file, nonce]);
  return chap.key === key ? chap : { key, json: null, err: false };
}

export default function BookReader({
  layout = 'split', book, heldAt = null, me = null, manifest = null,
  onAct, onFold = null, asleep = false, bot = false, curtain = false,
  bubbles = null, bottom = null,
}) {
  const open = isBookOpen(book);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  const serverNow = serverNowFor(book, heldAt, now);
  const seq = bookSeq(book);
  const seqRef = useRef(seq);
  seqRef.current = seq;
  const pages = Math.max(1, Number(book && book.pages) || 1);
  const [opt, setOpt] = useState(null); // { slug, page } while a turn is in flight
  const busyRef = useRef(false);
  const [note, setNote] = useState(null); // { text, at }
  const [asked, setAsked] = useState(null); // ms (local) when I asked
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!note) return undefined;
    const t = setTimeout(() => setNote(null), NOTE_MS);
    return () => clearTimeout(t);
  }, [note]);
  const flash = (text) => { if (text) setNote({ text: String(text), at: Date.now() }); };
  const slug = open ? book.book : null;
  const shown = Math.min(pages, Math.max(1, Number(opt && opt.slug === slug ? opt.page : (book && book.page)) || 1));

  // the chapter that holds the shown page: the manifest's map, else the payload's own file
  const entry = shelfBook(manifest, slug);
  const ch = entry ? chapterForPage(entry, shown)
    : (open && book.file && shown === Number(book.page) ? { file: book.file, label: book.chapter, first: shown, last: shown } : null);
  const chap = useChapter(open ? book.base_url : null, slug, ch && ch.file, retry);
  const paras = chap.json ? parasForPage(chap.json, shown) : null;
  const lastRef = useRef({ slug: null, paras: null });
  if (paras && slug) lastRef.current = { slug, paras };
  const stale = !paras && lastRef.current.slug === slug ? lastRef.current.paras : null;
  // the next chapter, a page before this one ends
  useEffect(() => {
    if (!entry || !ch || !wantsNextChapter(shown, ch)) return;
    const nx = chapterAfter(entry, ch);
    if (nx && nx.file) loadChapter(book.base_url, slug, nx.file).catch(() => {});
  }, [entry, ch && ch.file, shown]); // eslint-disable-line react-hooks/exhaustive-deps
  // a new page starts at its top
  const pageRef = useRef(null);
  useLayoutEffect(() => { if (pageRef.current) pageRef.current.scrollTop = 0; }, [shown, slug]);
  // a different book, or the state moved on: the guess goes
  useEffect(() => { setOpt(null); setAsked(null); }, [slug]);

  const mine = isHolder(book, me);
  const loose = bookmarkLoose(book, serverNow);
  const canTurn = canTurnPage(book, me, serverNow);
  const turn = useCallback(async (dir) => {
    if (busyRef.current || !open || typeof onAct !== 'function') return;
    if (!canTurnPage(book, me, serverNow)) { flash(`${bookmarkWords(book, me, serverNow)}. Ask for it.`); return; }
    const from = shown;
    const target = Math.min(pages, Math.max(1, from + dir));
    if (target === from) return;
    busyRef.current = true;
    setOpt({ slug, page: target });
    // the seq an answer carried (the hook has taken it, the render may not have happened yet)
    const seqOf = (r) => (r && r.book && typeof r.book === 'object' ? bookSeq(r.book) : seqRef.current);
    let res = await onAct('turn', { seq: seqRef.current, to: target });
    let retried = false;
    if (res && res.too_fast && Number(res.wait_ms) > 0 && Number(res.wait_ms) <= 2000) {
      await sleep(Number(res.wait_ms) + 60);
      res = await onAct('turn', { seq: seqOf(res), to: target });
    } else {
      // ⭐ a stale answer is the server's own state (the hook took it, even below our seq):
      // still on the page we turned from = turn again ONCE with its seq; moved = no double turn
      const again = staleTurnRetrySeq(res, slug, from);
      if (again != null) { retried = true; res = await onAct('turn', { seq: again, to: target }); }
    }
    busyRef.current = false;
    setOpt(null);
    if (!res || !res.ok) flash((res && res.error) || "The page didn't turn. Try again.");
    else if (res.too_fast) flash('One sec.');
    else if (res.stale && retried) flash("The page didn't turn. Try again.");
  }, [open, onAct, book, me, serverNow, pages, shown, slug]); // eslint-disable-line react-hooks/exhaustive-deps

  // ←/→ for the holder only (never while typing, never under a sheet)
  useEffect(() => {
    if (!mine || bot) return undefined;
    const onKey = (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || e.defaultPrevented) return;
      const t = e.target;
      const tag = t && t.tagName ? t.tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || (t && t.isContentEditable)) return;
      if (typeof document !== 'undefined' && document.querySelector('.rsh-scrim, [role="dialog"][aria-modal="true"]')) return;
      e.preventDefault();
      turn(e.key === 'ArrowRight' ? 1 : -1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mine, bot, turn]);

  const run = async (action, params = {}, after = null) => {
    if (typeof onAct !== 'function') return;
    const res = await onAct(action, params);
    if (!res || !res.ok) { flash((res && res.error) || 'That did not work. Try again.'); return; }
    if (res.too_fast) { flash('One sec.'); return; } // the per-room floor held it: nothing happened
    // an ask that was not recorded (another fresh ask, or the race was lost): never "Asked. Waiting…"
    if (action === 'ask' && res.busy) { flash('Someone else asked first.'); return; }
    if (res.stale) { flash('Someone got there first.'); return; } // the hook took the real state
    if (after) after(res);
  };

  if (bot) {
    // bot mode (the explorer reads the DOM): one inert line, no controls
    return open ? (
      <div data-book-reader="bot" style={{ padding: '8px 12px', borderRadius: 10, background: 'rgba(26,20,17,.9)', border: '1px solid #4d3b2e', color: '#f3e9d8', fontSize: 13, boxSizing: 'border-box' }}>
        📖 {book.title} is open in the library.
      </div>
    ) : null;
  }
  if (!open) return null;

  const ask = askForHolder(book, me, serverNow);
  const askedFresh = asked && Date.now() - asked < 12000 && !mine;
  const canTake = canTakeBookmark(book, me, serverNow);
  const canAsk = canAskBookmark(book, me, serverNow) && !askedFresh;
  const canClose = mine || loose;
  const chapterLabel = (ch && ch.label) || book.chapter || '';
  const phone = layout === 'phone';
  const text = paras || stale;
  return (
    <section className="hlb-rd" data-book-reader={layout} data-curtain={curtain ? '1' : undefined}
      aria-label={`${book.title}, ${pageLine(shown, pages)}`}
      style={phone ? { bottom: Number.isFinite(bottom) ? `${Math.max(0, window.innerHeight - bottom)}px` : 'calc(110px + env(safe-area-inset-bottom, 0px))' } : undefined}>
      <style>{READER_CSS}</style>
      {phone ? <div className="hlb-band" aria-hidden="true"><span>📚</span><span>Library</span></div> : null}
      <div className="hlb-top">
        <span className="hlb-ttl">{book.title}</span>
        <span className="hlb-ch">{chapterLabel}</span>
        {canClose && <button type="button" className="hlb-x" data-prop-ui="" data-book-close="1" onClick={() => run('close')}>Close</button>}
        {phone && onFold && <button type="button" className="hlb-x" data-prop-ui="" data-book-fold="1" aria-label="Show the chat" onClick={onFold}>Chat ▾</button>}
      </div>
      {asleep ? <div className="hlb-sleep" data-book-asleep="1">💤 The residents are asleep. The book still turns.</div> : null}
      <div className="hlb-pwrap">
        <div ref={pageRef} className="hlb-pg" data-book-page={shown} data-stale={!paras && stale ? '1' : undefined} tabIndex={-1}>
          {/* a failed (or timed out) chapter says so even over the last page's text, which stays dimmed under it */}
          {chap.err && !paras
            ? <p className="hlb-wait" data-book-stuck="1">This page is stuck. <button type="button" className="hlb-lnk" data-prop-ui="" data-book-retry="1" onClick={() => setRetry((n) => n + 1)}>Try again</button></p>
            : null}
          {text ? text.map((t, i) => (t.trim() === '* * *'
            ? <p key={i} className="hlb-brk" aria-hidden="true">* * *</p>
            : <p key={i}>{t}</p>))
            : chap.err ? null : <p className="hlb-wait">Finding the page…</p>}
        </div>
        {phone && Array.isArray(bubbles) && bubbles.length ? (
          <div className="hlb-bub" data-book-bubbles="1"><SceneBubbles lines={bubbles} stage /></div>
        ) : null}
      </div>
      {(ask || note || askedFresh) ? (
        <div className="hlb-row" data-book-note="1" aria-live="polite">
          {ask ? (<>
            <span><b>{ask.name}</b> asks for the bookmark</span>
            <span className="hlb-sp" />
            <button type="button" className="hlb-pill hlb-gold" data-prop-ui="" data-book-give="1" onClick={() => run('grant', { to: ask.seat })}>Give</button>
            <button type="button" className="hlb-pill" data-prop-ui="" data-book-keep="1" onClick={() => run('deny')}>Keep</button>
          </>) : (note ? <span>{note.text}</span> : <span>Asked. Waiting on {(book.holder && book.holder.name) || 'them'}.</span>)}
        </div>
      ) : null}
      <div className="hlb-ft">
        <button type="button" className="hlb-turn" data-prop-ui="" data-book-turn="prev" aria-label="Previous page" disabled={!canTurn || shown <= 1} onClick={() => turn(-1)}>‹</button>
        <span className="hlb-pn" data-book-pageline="1">{pageLine(shown, pages)}</span>
        <button type="button" className="hlb-turn" data-prop-ui="" data-book-turn="next" aria-label="Next page" disabled={!canTurn || shown >= pages} onClick={() => turn(1)}>›</button>
        <span className="hlb-mk" data-book-mark="1">{bookmarkWords(book, me, serverNow)}</span>
        {canTake && <button type="button" className="hlb-lnk" data-prop-ui="" data-book-take="1" onClick={() => run('take')}>Take the bookmark</button>}
        {canAsk && <button type="button" className="hlb-lnk" data-prop-ui="" data-book-ask="1" onClick={() => run('ask', {}, () => setAsked(Date.now()))}>Ask for it</button>}
      </div>
    </section>
  );
}
